import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import { CalendarPlus } from "lucide-react";
import { DatePicker } from "../../../../components/ui/DatePicker";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/Tabs";
import {
  FLOW_FORM_FIELD_LABEL_BASE,
  FLOW_TEXT_INPUT_BASE,
} from "../../../../styles/twDesignPrimitives";
import { ScheduleSearchResults } from "./ScheduleSearchResults";
import { useCreateCardFromSchedule } from "./useCreateCardFromSchedule";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => Promise<void> | void;
};

export default function CreateCardFromScheduleModal({
  isOpen,
  onClose,
  onCreated,
}: Props) {
  const {
    mode,
    setMode,
    clientQuery,
    setClientQuery,
    date,
    setDate,
    dateTo,
    setDateTo,
    setPage,
    items,
    meta,
    hasSearched,
    isSearching,
    rangeError,
    pendingScheduleId,
    setPendingScheduleId,
    creatingScheduleId,
    createCard,
  } = useCreateCardFromSchedule({ isOpen, onCreated });

  async function handleConfirmCreate(scheduleId: number) {
    const created = await createCard(scheduleId);
    if (created) onClose();
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      center
      classNames={{ modal: "create-card-from-schedule-modal" }}
    >
      <div className="flex w-full flex-col">
        <div className="flex items-start gap-3">
          <CalendarPlus className="mt-0.5 h-7 w-7 shrink-0 text-slate-700" aria-hidden />
          <div>
            <h2 className="text-lg font-bold text-slate-800 sm:text-xl">
              Criar card a partir de agendamento
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Busque a visita por cliente ou por data. Ao confirmar, o card é criado no
              Kanban, no Synkronos e no BPM.
            </p>
          </div>
        </div>

        <Tabs
          value={mode}
          onValueChange={(value) => setMode(value as "client" | "date")}
          className="mt-5"
        >
          <TabsList aria-label="Modo de busca de agendamento">
            <TabsTrigger value="client">Por cliente</TabsTrigger>
            <TabsTrigger value="date">Por data</TabsTrigger>
          </TabsList>

          <TabsContent value="client" card={false} className="mt-4">
            <label className={FLOW_FORM_FIELD_LABEL_BASE} htmlFor="schedule-card-client-search">
              Nome, e-mail ou telefone
            </label>
            <input
              id="schedule-card-client-search"
              type="search"
              value={clientQuery}
              onChange={(event) => setClientQuery(event.target.value)}
              placeholder="Ex.: Maria, e-mail ou telefone"
              className={FLOW_TEXT_INPUT_BASE}
            />
          </TabsContent>

          <TabsContent value="date" card={false} className="mt-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DatePicker
                label="Data"
                value={date}
                onChange={(value) => {
                  setDate(value);
                  setPage(1);
                }}
                placeholder="Selecionar data"
              />
              <DatePicker
                label="Até (opcional)"
                value={dateTo}
                onChange={(value) => {
                  setDateTo(value);
                  setPage(1);
                }}
                placeholder="Intervalo"
              />
            </div>
            {rangeError ? (
              <p className="mt-2 text-xs text-red-500">{rangeError}</p>
            ) : (
              <p className="mt-2 text-xs text-slate-400">
                Preencha só a data para um dia, ou as duas para um intervalo.
              </p>
            )}
          </TabsContent>
        </Tabs>

        <div className="mt-5">
          <ScheduleSearchResults
            items={items}
            meta={meta}
            isSearching={isSearching}
            hasSearched={hasSearched}
            pendingScheduleId={pendingScheduleId}
            creatingScheduleId={creatingScheduleId}
            onAskCreate={setPendingScheduleId}
            onCancelCreate={() => setPendingScheduleId(null)}
            onConfirmCreate={handleConfirmCreate}
            onPageChange={setPage}
          />
        </div>
      </div>
    </Modal>
  );
}
